import { createSelector } from 'reselect';
import { find, flatten, flow, get, keyBy, map, some, values, where } from 'lodash/fp';

import { isPlayerDisabled, makePositionStr } from '../utils/playerUtils';
import playerPositionsSelector from './playerPositionsSelector';
import teamPositionsSelector from './teamPositionsSelector';
import salaryRemainingSelector from './salaryRemainingSelector';
import selectionsBySideSelector from './selectionsBySideSelector';
import selectionsByGameSelector from './selectionsByGameSelector';

function annotatePlayers(
  players,
  teams,
  playerQuota,
  teamQuota,
  shareQuota,
  playersPerSideCap,
  playersPerGame,
  minGamesPerEntry,
  positionFilter,
  playerPositions,
  teamPositions,
  salaryRemaining,
  selectionsBySide,
  selectionsByGame
) {
  const selectedPlayers = flatten(map('selections', playerPositions));
  const selectedTeams = flatten(map('selections', teamPositions));

  const entities = values(players); // shareQuota ? [...values(players), ...values(teams)] : values(players);

  const isDisabled = isPlayerDisabled({
    playerQuota,
    teamQuota,
    playersSelected: selectedPlayers.length,
    teamsSelected: selectedTeams.length,
    playersPerSideCap,
    playersPerGame,
    minGamesPerEntry,
    playerPositions,
    teamPositions,
    salaryRemaining,
    selectionsBySide,
    selectionsByGame,
    shareQuota,
    positionFilter
  });

  const getPositionStr = makePositionStr(keyBy('id', playerPositions));
  const result = map(player => {
    const selectedPosition = flow(
      find(
        where({
          selections: some({ id: player.id })
        })
      ),
      get('id')
    )(playerPositions);

    return {
      ...player,
      disabled: selectedPosition
        ? !!positionFilter && selectedPosition !== positionFilter
        : isDisabled(player),
      positionStr: getPositionStr(player.positions) || get('positions[0].short_label', player),
      selected: positionFilter ? selectedPosition === positionFilter : !!selectedPosition,
      selectedPosition
    };
  }, entities);

  return result;
}

const playersSelector = createSelector(
  get('challenge.players'),
  get('challenge.teams'),
  get('challenge.playerQuota'),
  get('challenge.teamQuota'),
  get('challenge.shareQuota'),
  get('challenge.playersPerSideCap'),
  get('challenge.playersPerGame'),
  get('challenge.minGamesPerEntry'),
  get('lineUp.positionFilter'),
  playerPositionsSelector,
  teamPositionsSelector,
  salaryRemainingSelector,
  selectionsBySideSelector,
  selectionsByGameSelector,
  annotatePlayers
);

export default playersSelector;
